import { message } from "antd";
import { jwtDecode } from "jwt-decode";
import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import API from "../../../utils/api";

interface TokenState {
  token: string | null;
  updateToken: (token: string | null) => void;
  removeToken: () => void;
}

const useTokenStore = create<TokenState>()(
  persist(
    (set) => ({
      token: null,
      updateToken: (value) => set({ token: value }),
      removeToken: () => set({ token: null }),
    }),
    {
      name: "token-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export function useToken() {
  const token = useTokenStore((state) => state.token);
  const updateToken = useTokenStore((state) => state.updateToken);
  const removeToken = useTokenStore((state) => state.removeToken);

  const memoToken = useMemo(() => {
    return token;
  }, [token]);

  const userid = useMemo(() => {
    try {
      const decodedToken: any = jwtDecode(token + "");
      return decodedToken?.id || null;
    } catch (e) {
      return null;
    }
  }, [token]);

  const expire = useMemo(() => {
    try {
      const decodedToken: any = jwtDecode(token + "");
      return decodedToken?.exp || null;
    } catch (e) {
      return null;
    }
  }, [token]);

  const level = useMemo(() => {
    try {
      const decodedToken: any = jwtDecode(token + "");
      return decodedToken?.level || null;
    } catch (e) {
      return null;
    }
  }, [token]);

  useEffect(() => {
    if (level == -9) {
      message.error("Your account has been suspended.");
      removeToken();
    } else if (level == -2) {
      message.error("Your account has been Rejected.");
      removeToken();
    } else if (level == -10) {
      message.error("Your account has been Blacked.");
      removeToken();
    } else if (level == -8) {
      message.error("Your account is waiting for approval.");
      removeToken();
    } else if (level == -1) {
      message.error("Your account is waiting for approval.");
      removeToken();
    }
  }, [level]);

  return {
    token: memoToken,
    data: memoToken,
    level,
    userid,
    expire,
    refreshToken: async () => {
      const refresh = await API.refreshToken(token + "");
      if (refresh?.result) {
        message.success("Session has been refreshed.");
        const newtoken = refresh?.result;
        updateToken(newtoken);
      } else {
        message.error("Session has expired.");
        removeToken();
      }
    },
    mutate: (value: string | null) => {
      return updateToken(value);
    },
  };
}
